// FirstRunGuide — replaces the progress ring + nudge stack for users
// who haven't completed onboarding or logged anything yet. Avoids the
// "0.0 / 0.0 mg" hollow-shell impression of a fresh account.
//
// Three numbered steps map 1:1 to the onboarding → log → check-in
// flow. Disappears once the user has either locked a baseline or has
// >3 days of activity (whichever comes first).
//
// Phase 3 #15 — when `deferredMode` is true (user came in via the
// onboarding "Just log for now" branch), we also surface a dose-preset
// chip strip INSIDE the guide so the first tap from a fresh account
// is a dose, not "Continue." The chips call `onQuickLog(mg)` which
// the parent screen wires to POST /api/doses. Education stays — the
// numbered steps are preserved alongside the chips.

import { useNavigate } from 'react-router-dom';
import { Pill } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { DosePresetChip } from '../../components/domain/DoseChip';
import { COMMON_TABLET_STRENGTHS_MG } from '@shared/constants';
import { SAMPLE_PLAN_DEFAULTS } from '../../lib/deferredBaseline';

interface FirstRunGuideProps {
  /** Has a baseline been locked? Step 1 marks done. */
  baselineLocked: boolean;
  /** Has the user logged any dose at all? Step 2 marks done. */
  hasAnyDose: boolean;
  /** Has the user completed today's check-in? Step 3 marks done. */
  checkInDone: boolean;
  /**
   * Phase 3 #15 — render the deferred-baseline log-first surface.
   * When true, we show a "We picked some safe defaults" caption and
   * a quick-log chip strip BEFORE the numbered steps so the user's
   * first tap is a dose log, not a routing button.
   */
  deferredMode?: boolean;
  /**
   * Quick-log handler. Receives the chip's mg value and is wired by
   * the parent screen to POST /api/doses. Only used when deferredMode
   * is true.
   */
  onQuickLog?: (mg: number) => void;
}

interface Step {
  num: number;
  title: string;
  caption: string;
  done: boolean;
  onClick: () => void;
}

export function FirstRunGuide({
  baselineLocked,
  hasAnyDose,
  checkInDone,
  deferredMode = false,
  onQuickLog,
}: FirstRunGuideProps) {
  const navigate = useNavigate();

  const steps: Step[] = [
    {
      num: 1,
      title: 'Set your baseline',
      caption: 'Tell us your starting daily mg and we generate a taper plan.',
      done: baselineLocked,
      onClick: () => navigate('/app/onboarding'),
    },
    {
      num: 2,
      title: 'Log your first dose',
      caption: 'A tap or two. Builds the data your taper plan reads.',
      done: hasAnyDose,
      onClick: () => navigate({ pathname: '/app', hash: '#log' }),
    },
    {
      num: 3,
      title: "Tonight's 2-minute check-in",
      caption: 'How is your body? Sleep, cravings, mood. Sets up tomorrow.',
      done: checkInDone,
      onClick: () => navigate('/app/checkin'),
    },
  ];

  return (
    <Card>
      <h2 className="text-h2" style={{ marginBlockEnd: 'var(--space-2)' }}>
        Welcome.
      </h2>
      {deferredMode ? (
        <>
          <p className="muted" style={{ marginBlockEnd: 'var(--space-3)' }}>
            Log a dose to get started. Setup waits.
          </p>
          {/* In-memory sample-plan caption. We never persist these
              numbers — they exist only to give the user a concrete
              "target today" anchor while their first dose lands.
              Once they finish onboarding the real plan overrides
              everything. */}
          <p
            className="text-caption muted"
            style={{
              marginBlockEnd: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-bg-sunken)',
              borderRadius: 'var(--radius-sm)',
              borderInlineStart: '3px solid var(--color-accent)',
            }}
          >
            We picked some safe defaults — baseline {SAMPLE_PLAN_DEFAULTS.baselineMg} mg, target today{' '}
            {SAMPLE_PLAN_DEFAULTS.targetTodayMg} mg. You can change these once you've logged a dose.
          </p>
          {onQuickLog ? (
            <div style={{ marginBlockEnd: 'var(--space-4)' }}>
              <div className="scroll-strip">
                {COMMON_TABLET_STRENGTHS_MG.slice(0, 5).map((mg) => (
                  <DosePresetChip
                    key={mg}
                    label={`${mg} mg`}
                    onClick={() => onQuickLog(mg)}
                    holdToConfirm
                  />
                ))}
                <button
                  type="button"
                  className="dose-chip"
                  onClick={() => navigate({ pathname: '/app', hash: '#log' })}
                >
                  <Icon icon={Pill} size={16} />
                  Custom →
                </button>
              </div>
              <p
                className="dim text-caption"
                style={{ marginBlockStart: 'var(--space-2)', textAlign: 'center' }}
              >
                Tap and hold to log — a small pause before each dose.
              </p>
            </div>
          ) : null}
        </>
      ) : (
        <p className="muted" style={{ marginBlockEnd: 'var(--space-4)' }}>
          Three short steps to get the tracker working for you.
        </p>
      )}
      <div role="list">
        {steps.map((s) => (
          <div key={s.num} role="listitem" className="first-run-step">
            <span className="first-run-step-num">{s.done ? '✓' : s.num}</span>
            <div>
              <div className="first-run-step-title">{s.title}</div>
              <div className="first-run-step-caption">{s.caption}</div>
            </div>
            {s.done ? (
              <span className="text-caption muted">Done</span>
            ) : (
              <Button variant="tertiary" size="md" onClick={s.onClick}>
                Open →
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
